/*
 * The tray seal.
 *
 * This copies AYP Healthcare's current card to the millimetre: a Legal sheet, 28 blisters laid
 * out 4 weeks across and 7 days down, the coloured header bar for the time of day, and the
 * resident and pharmacy details along the bottom.
 *
 * Each blister lists only what actually belongs in it, so a weekly tablet appears on its own day
 * and nowhere else. Print on Legal, margins None, scale 100%, with background graphics on.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { TrayCard, TrayResident } from './trays.tsx';

/*
 * The barcode a carer scans before giving from the tray. It holds the resident, the time of day,
 * the cycle it was made up for and which card it is, so the tablet can tell them apart.
 */
function QrCode({ text }: { text: string }) {
  const [svg, setSvg] = useState('');

  useEffect(() => {
    let live = true;
    QRCode.toString(text, { type: 'svg', margin: 0, errorCorrectionLevel: 'M' })
      .then((result) => {
        if (live) setSvg(result);
      })
      .catch(() => setSvg(''));
    return () => {
      live = false;
    };
  }, [text]);

  return <div className="s-code-box" aria-label="Tray barcode" dangerouslySetInnerHTML={{ __html: svg }} />;
}

/* Measured from the pharmacy's current seal. Millimetres, from the top left of the page. */
const SEAL = {
  pageW: 215.9,
  pageH: 355.6,
  left: 24.6,
  top: 14.7,
  pitchX: 43.17,
  pitchY: 42.98,
  cell: 38.1,
  headerH: 5.6,
  nameH: 3.6,
  footerH: 3.6,
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function SealSheet({
  card,
  resident,
  home,
  pharmacy,
  cycleStart,
  offset,
}: {
  card: TrayCard;
  resident: TrayResident;
  home: { name: string; address: string[] };
  pharmacy: { name: string; address: string[]; phone: string };
  cycleStart: string;
  offset: { x: number; y: number };
}) {
  const cells = [];
  for (let week = 0; week < 4; week++) {
    for (let day = 0; day < 7; day++) {
      const date = addDays(cycleStart, week * 7 + day);
      const items = card.items.filter((item) => item.days.includes(date));
      cells.push({ week, day, date, items });
    }
  }

  return (
    <div className="seal-sheet" style={{ ['--shift-x' as string]: `${offset.x}mm`, ['--shift-y' as string]: `${offset.y}mm` }}>
      {/* The rotated day names down the left edge, as on the card */}
      {DAYS.map((name, day) => (
        <div
          key={name}
          className="s-day"
          style={{ top: `${SEAL.top + day * SEAL.pitchY + SEAL.cell / 2}mm` }}
        >
          {name}
        </div>
      ))}

      {cells.map(({ week, day, date, items }) => (
        <div
          key={`${week}-${day}`}
          className="s-cell"
          style={{
            left: `${SEAL.left + week * SEAL.pitchX}mm`,
            top: `${SEAL.top + day * SEAL.pitchY}mm`,
            width: `${SEAL.cell}mm`,
            height: `${SEAL.cell}mm`,
          }}
        >
          <div className="s-header" style={{ background: card.colour, height: `${SEAL.headerH}mm` }} />
          <div className="s-name" style={{ height: `${SEAL.nameH}mm` }}>{resident.residentName}</div>
          <div className="s-meds" style={{ fontSize: medSize(items.length) }}>
            {items.map((item) => (
              <div key={item.medicineId}>{shortName(item)}</div>
            ))}
          </div>
          <div className="s-footer" style={{ height: `${SEAL.footerH}mm` }}>
            {card.sealName}- {sealDate(date)}
          </div>
        </div>
      ))}

      <div className="s-warning">Keep out of the reach and sight of children</div>

      <div className="s-tray" style={{ borderColor: card.colour }}>
        <strong>{card.kind === 'separate' ? 'Separate' : 'Tray'}</strong>
        <span>{card.number} of {card.of}</span>
      </div>

      <div className="s-resident">
        <strong>{resident.residentName}</strong>
        {resident.room ? <span>Room {resident.room}</span> : null}
        <span>{home.name}</span>
        {home.address.map((line) => <span key={line}>{line}</span>)}
      </div>

      <div className="s-pharmacy">
        <strong>{pharmacy.name}</strong>
        {pharmacy.address.map((line) => <span key={line}>{line}</span>)}
        {pharmacy.phone ? <span>{pharmacy.phone}</span> : null}
      </div>

      <div className="s-code">
        <QrCode text={card.code} />
        <span className="s-code-text">{card.sealName} {card.number}/{card.of}</span>
      </div>

      <div className="s-printed">
        {card.items.length} medicine{card.items.length === 1 ? '' : 's'} · printed {sealDate(todayKey())}
      </div>
    </div>
  );
}

/*
 * The alignment page. Print it on plain paper, hold it over an empty card against a window, and
 * see how far out this printer is. The rulers along the top and side read off the difference.
 */
export function AlignmentSheet({ offset }: { offset: { x: number; y: number } }) {
  const cells = [];
  for (let week = 0; week < 4; week++) {
    for (let day = 0; day < 7; day++) cells.push({ week, day });
  }

  return (
    <div
      className="seal-sheet alignment"
      style={{ ['--shift-x' as string]: `${offset.x}mm`, ['--shift-y' as string]: `${offset.y}mm` }}
    >
      {cells.map(({ week, day }) => (
        <div
          key={`${week}-${day}`}
          className="s-outline"
          style={{
            left: `${SEAL.left + week * SEAL.pitchX}mm`,
            top: `${SEAL.top + day * SEAL.pitchY}mm`,
            width: `${SEAL.cell}mm`,
            height: `${SEAL.cell}mm`,
          }}
        >
          <span>{week * 7 + day + 1}</span>
        </div>
      ))}

      {/* A 10mm ruler along the top and down the left, for measuring the shift. */}
      {Array.from({ length: 21 }, (_, i) => i * 10).map((mm) => (
        <div key={`x${mm}`} className="s-tick x" style={{ left: `${mm}mm` }}>{mm}</div>
      ))}
      {Array.from({ length: 35 }, (_, i) => i * 10).map((mm) => (
        <div key={`y${mm}`} className="s-tick y" style={{ top: `${mm}mm` }}>{mm}</div>
      ))}

      <div className="s-align-note">
        <strong>Alignment test</strong>
        <span>Hold this over an empty tray card against a window.</span>
        <span>The first box should sit 24.6mm from the left and 14.7mm from the top.</span>
        <span>Currently shifted {offset.x}mm across and {offset.y}mm down.</span>
        <span>Print on Legal, margins None, scale 100%, background graphics on.</span>
      </div>
    </div>
  );
}

/* Pharmacy-label shorthand, so a long name still fits in a blister. */
function shortName(item: { name: string; qty: number | null; doseText: string }): string {
  const name = item.name
    .replace(/modified-release/i, 'MR')
    .replace(/gastro-resistant/i, 'GR')
    .replace(/micrograms?/i, 'mcg')
    .replace(/\btablets?\b/i, 'tabs')
    .replace(/\bcapsules?\b/i, 'caps')
    .replace(/\bsolution\b/i, 'soln');
  const amount = item.qty ?? '';
  return `${amount} ${name}`.trim();
}

function medSize(count: number): string {
  if (count <= 4) return '8pt';
  if (count <= 6) return '7pt';
  return '6pt';
}

/* The date as it is printed on a blister: "Mon 14.09.26". */
function sealDate(date: string): string {
  const when = new Date(`${date}T12:00:00`);
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = when.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${day} ${pad(when.getDate())}.${pad(when.getMonth() + 1)}.${String(when.getFullYear()).slice(2)}`;
}

function addDays(date: string, days: number): string {
  const when = new Date(`${date}T12:00:00`);
  when.setDate(when.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
}

function todayKey(): string {
  return addDays(new Date().toISOString().slice(0, 10), 0);
}

export { SEAL };
